/**
 * Per-manager order edit policy (UI side).
 *
 * A manager ("gestore") can be limited in what its users may change on
 * orders: which fields, and which actions (create, cancel, group, duplicate,
 * move in the timeline). The policy lives on `gestori/{id}.edit_policy`, is
 * set by MBILITY admins only and binds users whose role is `gestore`.
 *
 * Twin of the backend `functions/utils/orderEditPolicy.js` (API guard); the
 * same contract is enforced for direct client writes by `firestore.rules`.
 * Shared fixtures: `__tests__/fixtures/orderEditPolicy.fixtures.json`.
 * See docs/architecture/order-edit-policy.md.
 */

export const ORDER_EDIT_PROFILES = {
  FULL: 'full',
  DRIVER_ONLY: 'driver_only',
  READ_ONLY: 'read_only',
};

export const ORDER_EDIT_ACTIONS = ['create', 'cancel', 'group', 'duplicate', 'move'];

/** Fields that record who/when changed an order; never a business change. */
export const ORDER_SYSTEM_FIELDS = [
  'history',
  'updated_by',
  '_updated_by',
  'updated_at',
  'last_update',
];

/** Fields an enabled action unlocks: the service time, the cancellation cause. */
export const ACTION_FIELDS = {
  move: ['data_esecuzione', 'ora'],
  cancel: ['cancellation_reason', 'cancellation_note', 'cancelled_at'],
};

/** Assignment status transition a driver change is allowed to carry. */
const ASSIGNABLE_FROM = [1, 2, 3];
const ASSIGNABLE_TO = [1, 2];
const CANCELLED_STATUS = 6;

const noActions = () => Object.fromEntries(ORDER_EDIT_ACTIONS.map((a) => [a, false]));

export const PROFILE_PRESETS = {
  [ORDER_EDIT_PROFILES.DRIVER_ONLY]: {
    editable_fields: [
      'id_autista',
      'nome_autista',
      'secondo_operatore',
      'secondo_operatore_arrivo',
    ],
    actions: noActions(),
  },
  [ORDER_EDIT_PROFILES.READ_ONLY]: {
    editable_fields: [],
    actions: noActions(),
  },
};

const UNRESTRICTED = Object.freeze({
  profile: ORDER_EDIT_PROFILES.FULL,
  restricted: false,
  editable_fields: [],
  actions: Object.fromEntries(ORDER_EDIT_ACTIONS.map((a) => [a, true])),
});

/**
 * Resolves the stored policy of a manager. A missing policy, or the `full`
 * profile, means no restriction. Stored `editable_fields`/`actions` win over
 * the preset of the profile; an unknown profile is treated as read-only so a
 * typo never opens everything.
 */
export const resolveOrderEditPolicy = (manager) => {
  const stored = manager?.edit_policy;
  const profile = stored?.profile || ORDER_EDIT_PROFILES.FULL;
  if (profile === ORDER_EDIT_PROFILES.FULL) return UNRESTRICTED;

  const preset = PROFILE_PRESETS[profile] || PROFILE_PRESETS[ORDER_EDIT_PROFILES.READ_ONLY];
  const editable = Array.isArray(stored?.editable_fields)
    ? stored.editable_fields
    : preset.editable_fields;
  const actions = { ...noActions(), ...preset.actions };
  ORDER_EDIT_ACTIONS.forEach((a) => {
    if (typeof stored?.actions?.[a] === 'boolean') actions[a] = stored.actions[a];
  });
  return { profile, restricted: true, editable_fields: editable.filter(Boolean), actions };
};

/**
 * True when the policy applies to the user. `role` is the role object stored
 * on the user (`{ gestore: true, admin: false, ... }`).
 */
export const isPolicyBoundRole = (role) =>
  role?.gestore === true &&
  role?.admin !== true &&
  role?.administrative !== true &&
  role?.super !== true;

export const canDoOrderAction = (policy, action) =>
  !policy?.restricted || policy.actions?.[action] === true;

export const canEditOrderField = (policy, field) =>
  !policy?.restricted ||
  ORDER_SYSTEM_FIELDS.includes(field) ||
  policy.editable_fields.includes(field) ||
  Object.entries(ACTION_FIELDS).some(
    ([action, fields]) => policy.actions?.[action] === true && fields.includes(field)
  );

const toComparable = (value) => {
  if (value && typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (value && typeof value === 'object' && typeof value._seconds === 'number') {
    return value._seconds * 1000 + Math.floor((value._nanoseconds || 0) / 1e6);
  }
  return value;
};

const sameValue = (a, b) => {
  const x = toComparable(a);
  const y = toComparable(b);
  if (x === y) return true;
  if (x == null || y == null) return (x ?? null) === (y ?? null);
  if (typeof x !== 'object' || typeof y !== 'object') return false;
  if (Array.isArray(x) !== Array.isArray(y)) return false;
  const keys = new Set([...Object.keys(x), ...Object.keys(y)]);
  for (const k of keys) if (!sameValue(x[k], y[k])) return false;
  return true;
};

/** Keys of `patch` whose value differs from `prev` (update semantics). */
export const changedOrderKeys = (prev, patch) =>
  Object.keys(patch || {}).filter((k) => k !== 'id' && !sameValue(prev?.[k], patch[k]));

/** Whether a status change from→to is allowed by the policy. */
export const isStatusChangeAllowed = (policy, from, to) => {
  if (!policy?.restricted || from === to) return true;
  if (to === CANCELLED_STATUS) return canDoOrderAction(policy, 'cancel');
  return (
    policy.editable_fields.includes('id_autista') &&
    ASSIGNABLE_FROM.includes(from) &&
    ASSIGNABLE_TO.includes(to)
  );
};

/**
 * Checks a partial update against the policy.
 * @returns {{ ok: boolean, forbidden: string[] }}
 */
export const checkOrderPatch = (policy, prev, patch) => {
  if (!policy?.restricted) return { ok: true, forbidden: [] };
  const forbidden = changedOrderKeys(prev, patch).filter((k) => {
    if (k === 'status') return !isStatusChangeAllowed(policy, prev?.status, patch.status);
    return !canEditOrderField(policy, k);
  });
  return { ok: forbidden.length === 0, forbidden };
};

/**
 * Keeps only the fields the policy lets the user change. Used where the
 * client sends a whole order (the detail form): values that did not change
 * are dropped too, so a serialisation difference never counts as an edit.
 * @returns {{ allowed: Object, ignored: string[] }}
 */
export const pickEditableOrderFields = (policy, prev, patch) => {
  if (!policy?.restricted) return { allowed: { ...patch }, ignored: [] };
  const allowed = {};
  const ignored = [];
  changedOrderKeys(prev, patch).forEach((k) => {
    const ok =
      k === 'status'
        ? isStatusChangeAllowed(policy, prev?.status, patch.status)
        : canEditOrderField(policy, k) && !ORDER_SYSTEM_FIELDS.includes(k);
    if (ok) allowed[k] = patch[k];
    else ignored.push(k);
  });
  return { allowed, ignored };
};
